import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Upload, RefreshCw, Search, Filter, Inbox, Users, MessageCircle,
  CheckCircle2, XCircle, Clock, Phone, ShoppingBag, TrendingUp,
  Loader2, FileText, BarChart3, Trash2, UserPlus,
} from 'lucide-react';
import { marketplaceLeads, dealerBilling, marketplaceActor } from '../../services/dealerApi';
import { toast } from '../../hooks/use-toast';
import WhatsAppHealthCard from '../../components/dealers/WhatsAppHealthCard';
import { formatUtcDatetime, formatUtcDate } from '../../utils/formatUtcDate';
import LeadUpdateModal from '../../components/dealers/LeadUpdateModal';
import ManualLeadModal from '../../components/dealers/ManualLeadModal';

const STATUS_LABELS = {
  new: { label: 'New', color: 'slate', icon: Clock },
  queued: { label: 'Queued', color: 'slate', icon: Clock },
  sent: { label: 'Sent', color: 'blue', icon: MessageCircle },
  delivered: { label: 'Delivered · No Response', color: 'orange', icon: Inbox },
  failed: { label: 'Failed Number', color: 'red', icon: XCircle },
  responded: { label: 'Responded', color: 'green', icon: CheckCircle2 },
  no_response: { label: 'Delivered · No Response', color: 'orange', icon: Inbox },
  sold: { label: 'Sold', color: 'purple', icon: ShoppingBag },
  lost: { label: 'Lost', color: 'red', icon: XCircle },
};

const SOURCES = ['CarDekho', 'CarWale', 'CarTrade', 'OLX', 'Quikr', 'Droom', 'Spinny', 'Dealer Website', 'Propitstop Marketplace'];

const TABS = [
  { id: 'all', label: 'All', filter: {} },
  { id: 'responded', label: 'Responded', filter: { status: 'responded' } },
  { id: 'no_response', label: 'No Response', filter: { status: 'delivered' } },
  { id: 'failed', label: 'Failed', filter: { status: 'failed' } },
  { id: 'sold', label: 'Sold', filter: { status: 'sold' } },
];

const StatusPill = ({ status }) => {
  const cfg = STATUS_LABELS[status] || STATUS_LABELS.new;
  const Icon = cfg.icon;
  const cls = {
    slate: 'bg-slate-800 text-slate-300 border-slate-700',
    blue: 'bg-blue-500/10 text-blue-300 border-blue-500/30',
    orange: 'bg-orange-500/10 text-orange-300 border-orange-500/30',
    red: 'bg-red-500/10 text-red-300 border-red-500/30',
    green: 'bg-green-500/10 text-green-300 border-green-500/30',
    purple: 'bg-purple-500/10 text-purple-300 border-purple-500/30',
  }[cfg.color];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${cls}`}>
      <Icon className="w-3 h-3" /> {cfg.label}
    </span>
  );
};

const StatTile = ({ label, value, accent = 'cyan', icon: Icon }) => (
  <div className="relative bg-slate-900/40 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-4 overflow-hidden">
    <div className={`absolute -top-10 -right-10 w-28 h-28 rounded-full bg-${accent}-500/10 blur-2xl pointer-events-none`} />
    <div className="relative flex items-start justify-between">
      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-slate-400 font-mono">{label}</div>
        <div className="mt-2 text-2xl font-extrabold text-white">{value}</div>
      </div>
      {Icon && (
        <div className={`w-9 h-9 rounded-xl bg-${accent}-500/10 border border-${accent}-500/20 flex items-center justify-center`}>
          <Icon className={`w-4 h-4 text-${accent}-400`} />
        </div>
      )}
    </div>
  </div>
);

const MarkSoldModal = ({ open, lead, onClose, onConfirm, busy }) => {
  const [src, setSrc] = useState('');
  const [notes, setNotes] = useState('');
  useEffect(() => {
    if (open) {
      setSrc(lead?.sources?.[0]?.source || '');
      setNotes('');
    }
  }, [open, lead]);
  if (!open) return null;
  const sourceOptions = Array.from(new Set([
    ...(lead?.sources?.map((s) => s.source) || []),
    ...SOURCES,
  ]));
  return (
    <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur flex items-center justify-center p-4" onClick={onClose}>
      <div className="w-full max-w-md bg-slate-900 border border-slate-700 rounded-2xl p-6" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-bold text-white mb-1">Mark as sold</h3>
        <p className="text-sm text-slate-400 mb-4">Which source actually converted {lead?.customer_name}?</p>
        <label className="block text-[10px] uppercase tracking-[0.22em] text-slate-500 mb-1.5 font-mono">Converted via</label>
        <select value={src} onChange={(e) => setSrc(e.target.value)} className="w-full bg-slate-950/60 border border-slate-700 rounded-lg px-3 py-2.5 text-sm text-white" data-testid="mark-sold-source">
          {sourceOptions.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <label className="block text-[10px] uppercase tracking-[0.22em] text-slate-500 mt-4 mb-1.5 font-mono">Notes (optional)</label>
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="w-full bg-slate-950/60 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white" placeholder="e.g. Sold the 2021 Honda City after 3 follow-ups" data-testid="mark-sold-notes" />
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-700 text-slate-300 text-sm font-semibold">Cancel</button>
          <button
            disabled={!src || busy}
            onClick={() => onConfirm({ sold_via_source: src, notes })}
            data-testid="mark-sold-confirm"
            className="px-4 py-2 rounded-lg bg-purple-500 hover:bg-purple-400 text-white text-sm font-bold disabled:opacity-60"
          >
            {busy ? 'Saving…' : 'Mark Sold'}
          </button>
        </div>
      </div>
    </div>
  );
};

const MarketplaceLeads = () => {
  const [leads, setLeads] = useState([]);
  const [analytics, setAnalytics] = useState({ total: 0, by_status: {}, by_source: {}, sold_by_source: {}, by_response_type: {}, whatsapp_funnel: { sent: 0, delivered: 0, read: 0, replied: 0, button_clicked: 0 } });
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [telecaller, setTelecaller] = useState(false);
  const [soldModal, setSoldModal] = useState({ open: false, lead: null });
  const [soldBusy, setSoldBusy] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [waDiag, setWaDiag] = useState(null);
  const [usage, setUsage] = useState(null);
  const [actor, setActor] = useState(null);
  const [updateModalLead, setUpdateModalLead] = useState(null);
  const [manualOpen, setManualOpen] = useState(false);
  const fileRef = useRef(null);

  const loadUsage = async () => {
    // Run both fetches independently so a failure in one doesn't stop the other.
    // (E.g. /billing/usage 401 must not prevent /marketplace-leads/me from setting
    // the actor context that controls the telecaller banner + hidden buttons.)
    const [usageRes, actorRes] = await Promise.allSettled([
      dealerBilling.usage(),
      marketplaceActor.me(),
    ]);
    if (usageRes.status === 'fulfilled') setUsage(usageRes.value);
    if (actorRes.status === 'fulfilled') setActor(actorRes.value);
  };

  const handleSyncTwilio = async () => {
    setSyncing(true);
    try {
      const res = await marketplaceLeads.syncTwilioStatus();
      if (res.last_error) {
        setWaDiag(res.last_error);
        toast({
          title: `${res.failures} message(s) failed at Twilio`,
          description: res.last_error.hint || res.last_error.error_message || 'Check WhatsApp sender registration.',
          variant: 'destructive',
        });
      } else if (res.updated > 0) {
        setWaDiag(null);
        toast({ title: `Synced ${res.updated} message(s) from Twilio` });
      } else {
        toast({ title: 'Up to date · no changes from Twilio' });
      }
      load();
    } catch (err) {
      toast({ title: 'Sync failed', description: err.response?.data?.detail || 'Could not reach Twilio', variant: 'destructive' });
    } finally {
      setSyncing(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const tabFilter = TABS.find((t) => t.id === activeTab)?.filter || {};
      const params = { ...tabFilter };
      if (sourceFilter !== 'all') params.source = sourceFilter;
      if (telecaller) params.telecaller_view = true;
      const [list, an] = await Promise.all([
        marketplaceLeads.list(params),
        marketplaceLeads.analytics(),
      ]);
      setLeads(list.leads || []);
      setAnalytics(an);

      // Auto-sync from Twilio in the background if anything is still queued — surfaces
      // upstream failures without the dealer needing to click anything. Skip for
      // telecallers (endpoint is main-dealer-only).
      const hasQueued = (list.leads || []).some((l) =>
        ['queued', 'sent', 'sending', 'accepted'].includes((l.last_message_status || '').toLowerCase()),
      );
      if (hasQueued && !syncing && actor?.actor_type !== 'telecaller') {
        try {
          const sync = await marketplaceLeads.syncTwilioStatus();
          if (sync.last_error) setWaDiag(sync.last_error);
        } catch { /* silent — sync is best-effort */ }
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadUsage(); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeTab, sourceFilter, telecaller]);

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const res = await marketplaceLeads.upload(file);
      const capNote = res.acks_blocked_by_cap > 0 ? ` · ${res.acks_blocked_by_cap} blocked by monthly cap` : '';
      toast({
        title: `Uploaded · ${res.new_leads} new lead${res.new_leads === 1 ? '' : 's'}`,
        description: `${res.merged_duplicates} duplicates merged · ${res.acks_queued} WhatsApp acks queued${capNote} · Source: ${res.detected_source}`,
        variant: res.acks_blocked_by_cap > 0 ? 'destructive' : undefined,
      });
      load();
      loadUsage();
    } catch (err) {
      toast({ title: 'Upload failed', description: err.response?.data?.detail || 'Could not parse file', variant: 'destructive' });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleClearAll = async () => {
    const total = analytics.total || 0;
    if (total === 0) {
      toast({ title: 'Nothing to clear' });
      return;
    }
    const ok = window.confirm(
      `Permanently delete ALL ${total} marketplace lead${total === 1 ? '' : 's'} and their WhatsApp history?\n\nThis cannot be undone.`
    );
    if (!ok) return;
    setClearing(true);
    try {
      const res = await marketplaceLeads.clearAll();
      toast({
        title: `Cleared ${res.leads_deleted} lead${res.leads_deleted === 1 ? '' : 's'}`,
        description: `${res.messages_deleted} WhatsApp record${res.messages_deleted === 1 ? '' : 's'} also removed`,
      });
      load();
    } catch (err) {
      toast({ title: 'Clear failed', description: err.response?.data?.detail || '', variant: 'destructive' });
    } finally {
      setClearing(false);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return leads;
    return leads.filter((l) =>
      `${l.customer_name} ${l.mobile_number} ${l.interested_car || ''} ${l.sources?.map((s) => s.source).join(' ')}`.toLowerCase().includes(q)
    );
  }, [leads, search]);

  const handleMarkSold = async (payload) => {
    setSoldBusy(true);
    try {
      await marketplaceLeads.markSold(soldModal.lead.id, payload);
      toast({ title: 'Marked as sold' });
      setSoldModal({ open: false, lead: null });
      load();
    } catch {
      toast({ title: 'Failed', variant: 'destructive' });
    } finally {
      setSoldBusy(false);
    }
  };

  const counts = analytics.by_status || {};
  const respondedTotal = counts.responded || 0;
  const noResponseTotal = (counts.delivered || 0) + (counts.no_response || 0);
  const failedTotal = counts.failed || 0;
  const soldTotal = counts.sold || 0;

  return (
    <div className="space-y-6" data-testid="marketplace-leads-page">
      {/* WhatsApp Health — only visible to main dealer (endpoint is main-dealer-only). */}
      {actor?.actor_type !== 'telecaller' && <WhatsAppHealthCard />}

      {/* Telecaller context banner */}
      {actor?.actor_type === 'telecaller' && (
        <div className="p-3 rounded-xl bg-cyan-500/5 border border-cyan-500/30 text-sm text-cyan-200 flex items-center justify-between" data-testid="telecaller-context">
          <span>
            Signed in as <strong className="text-white">{actor.telecaller?.name}</strong>
            {actor.telecaller?.source_tag && <> · Source: <strong className="text-white">{actor.telecaller.source_tag}</strong></>}
            {' '}· You only see leads you upload.
          </span>
        </div>
      )}

      {/* Compact billing/cap strip */}
      {usage && (
        <div
          className={`p-3 rounded-xl border flex flex-wrap items-center gap-3 text-sm ${usage.blocked ? 'border-red-500/40 bg-red-500/5 text-red-200' : usage.warning ? 'border-amber-500/40 bg-amber-500/5 text-amber-200' : 'border-slate-800 bg-slate-900/40 text-slate-300'}`}
          data-testid="cap-strip"
        >
          <MessageCircle className={`w-4 h-4 ${usage.blocked ? 'text-red-400' : usage.warning ? 'text-amber-400' : 'text-cyan-400'}`} />
          <span>
            <strong className="text-white tabular-nums">{usage.sent.toLocaleString()}</strong> / {usage.cap.toLocaleString()} messages this cycle
            <span className="text-slate-500"> · {formatUtcDate(usage.cycle_start)} → {formatUtcDate(usage.cycle_end)}</span>
          </span>
          <div className="flex-1 min-w-[100px] h-1.5 bg-slate-800 rounded-full overflow-hidden">
            <div
              className={`h-full ${usage.blocked ? 'bg-red-500' : usage.warning ? 'bg-amber-500' : 'bg-cyan-500'}`}
              style={{ width: `${Math.min(100, (usage.sent / Math.max(usage.cap, 1)) * 100)}%` }}
            />
          </div>
          {usage.blocked && <span className="px-2 py-0.5 rounded-full bg-red-500/20 text-red-200 text-xs font-bold">CAP REACHED</span>}
        </div>
      )}

      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <span className="text-xs uppercase tracking-[0.2em] text-cyan-400 font-medium">Marketplace Leads</span>
          <h1 className="mt-1 text-3xl md:text-4xl font-extrabold text-white tracking-tight">Lead Inbox</h1>
          <p className="mt-1 text-slate-400 text-sm">
            Upload CarDekho · CarWale · CarTrade · OLX exports. We auto-clean, dedupe, WhatsApp, and track replies.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl cursor-pointer bg-slate-900/60 border border-slate-700 text-sm text-slate-300 ${telecaller ? '!border-cyan-500/60 !text-cyan-300' : ''}`}>
            <input
              type="checkbox"
              checked={telecaller}
              onChange={(e) => setTelecaller(e.target.checked)}
              className="accent-cyan-500"
              data-testid="telecaller-toggle"
            />
            Telecaller mode
          </label>
          <button
            onClick={load}
            data-testid="leads-refresh"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-slate-900/60 border border-slate-700 text-sm text-slate-300"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
          {actor?.actor_type !== 'telecaller' && (
            <button
              onClick={handleSyncTwilio}
              disabled={syncing}
              data-testid="sync-twilio-btn"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 text-sm text-amber-300 disabled:opacity-40 transition-colors"
              title="Pull fresh WhatsApp delivery status from Twilio (useful when status webhooks are blocked)"
            >
              {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageCircle className="w-4 h-4" />}
              {syncing ? 'Syncing…' : 'Sync WhatsApp'}
            </button>
          )}
          <button
            onClick={handleClearAll}
            disabled={clearing || analytics.total === 0}
            data-testid="clear-all-btn"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 text-sm text-red-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            title={analytics.total === 0 ? 'No leads to clear' : 'Permanently delete all leads'}
          >
            {clearing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
            {clearing ? 'Clearing…' : 'Clear All'}
          </button>
          <button
            onClick={() => setManualOpen(true)}
            data-testid="add-manual-lead-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 text-sm font-bold transition-colors"
            title="Type a lead in manually (with voice-to-text)"
          >
            <UserPlus className="w-4 h-4" /> Add Lead
          </button>
          <label className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl cursor-pointer bg-cyan-500 hover:bg-cyan-400 text-slate-950 text-sm font-bold transition-colors ${uploading ? 'opacity-60 pointer-events-none' : ''}`}>
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {uploading ? 'Uploading…' : 'Upload CSV / Excel'}
            <input
              ref={fileRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              className="hidden"
              onChange={(e) => handleUpload(e.target.files?.[0])}
              data-testid="upload-input"
            />
          </label>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 sm:gap-4">
        <StatTile label="Total" value={analytics.total} icon={Users} accent="cyan" />
        <StatTile label="Responded" value={respondedTotal} icon={CheckCircle2} accent="green" />
        <StatTile label="No Response" value={noResponseTotal} icon={Inbox} accent="orange" />
        <StatTile label="Failed" value={failedTotal} icon={XCircle} accent="red" />
        <StatTile label="Sold" value={soldTotal} icon={ShoppingBag} accent="purple" />
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1.5 border-b border-slate-800">
        {TABS.map((t) => {
          const active = activeTab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              data-testid={`tab-${t.id}`}
              className={`px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors ${active ? 'border-cyan-400 text-white' : 'border-transparent text-slate-400 hover:text-slate-200'}`}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, mobile, car, source…"
            data-testid="leads-search"
            className="w-full pl-9 pr-3 py-2.5 rounded-xl bg-slate-900/60 border border-slate-700 focus:border-cyan-500/50 focus:outline-none text-sm text-white placeholder:text-slate-500"
          />
        </div>
        <div className="flex items-center gap-1.5">
          <Filter className="w-4 h-4 text-slate-500" />
          <select
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
            data-testid="source-filter"
            className="bg-slate-900/60 border border-slate-700 text-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none"
          >
            <option value="all">All sources</option>
            {Object.keys(analytics.by_source || {}).sort().map((s) => (
              <option key={s} value={s}>{s} ({analytics.by_source[s]})</option>
            ))}
          </select>
        </div>
      </div>

      {/* Source analytics chips */}
      {Object.keys(analytics.by_source || {}).length > 0 && (
        <div className="flex flex-wrap gap-2 text-[11px]">
          {Object.entries(analytics.by_source).sort((a, b) => b[1] - a[1]).map(([src, n]) => (
            <span key={src} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-slate-900/60 border border-slate-700 text-slate-300">
              <BarChart3 className="w-3 h-3 text-cyan-400" /> {src}: <strong className="text-white">{n}</strong>
              {analytics.sold_by_source?.[src] ? <span className="text-purple-300">· {analytics.sold_by_source[src]} sold</span> : null}
            </span>
          ))}
        </div>
      )}

      {/* WhatsApp Funnel + Customer Response analytics */}
      {(analytics.whatsapp_funnel || analytics.by_response_type) && (
        <div className="grid lg:grid-cols-2 gap-3" data-testid="whatsapp-analytics">
          <div className="bg-slate-900/40 border border-slate-700/50 rounded-2xl p-4">
            <div className="text-[10px] uppercase tracking-[0.22em] text-slate-400 font-mono mb-3">WhatsApp Delivery Funnel</div>
            <div className="grid grid-cols-5 gap-2">
              {[
                { key: 'sent', label: 'Sent', color: 'text-slate-200' },
                { key: 'delivered', label: 'Delivered', color: 'text-cyan-300' },
                { key: 'read', label: 'Read', color: 'text-blue-300' },
                { key: 'button_clicked', label: 'Button', color: 'text-amber-300' },
                { key: 'replied', label: 'Replied', color: 'text-emerald-300' },
              ].map((m) => (
                <div key={m.key} data-testid={`funnel-${m.key}`} className="rounded-xl bg-slate-950/60 border border-slate-800 p-3 text-center">
                  <div className="text-[9px] uppercase tracking-wider text-slate-500">{m.label}</div>
                  <div className={`text-lg font-bold mt-1 ${m.color}`}>{analytics.whatsapp_funnel?.[m.key] ?? 0}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-slate-900/40 border border-slate-700/50 rounded-2xl p-4">
            <div className="text-[10px] uppercase tracking-[0.22em] text-slate-400 font-mono mb-3">Customer Responses</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {[
                { key: 'enquired',          label: 'Yes, I Enquired',     color: 'text-emerald-300' },
                { key: 'needs_assistance',  label: 'Needs Assistance',    color: 'text-cyan-300' },
                { key: 'not_interested',    label: 'Not Interested',      color: 'text-amber-300' },
                { key: 'no_longer_looking', label: 'No Longer Looking',   color: 'text-red-300' },
              ].map((m) => (
                <div key={m.key} data-testid={`response-${m.key}`} className="rounded-xl bg-slate-950/60 border border-slate-800 p-3">
                  <div className="text-[9px] uppercase tracking-wider text-slate-500 leading-tight">{m.label}</div>
                  <div className={`text-lg font-bold mt-1 ${m.color}`}>{analytics.by_response_type?.[m.key] ?? 0}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="py-12 text-center text-slate-500">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="py-14 text-center border border-dashed border-slate-700/60 rounded-2xl bg-slate-900/30">
          <FileText className="w-10 h-10 mx-auto mb-2 text-slate-600" />
          <p className="text-white font-semibold">No leads in this view</p>
          <p className="text-sm text-slate-400 mt-1">Upload a CSV/Excel from CarDekho, OLX, CarWale etc. to get started.</p>
        </div>
      ) : (
        <div className="bg-slate-900/40 backdrop-blur-xl border border-slate-700/50 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="leads-table">
              <thead className="bg-slate-900/60 text-slate-400 text-[10px] uppercase tracking-[0.18em]">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Customer</th>
                  <th className="text-left px-4 py-3 font-semibold">Mobile</th>
                  <th className="text-left px-4 py-3 font-semibold">Interested In</th>
                  <th className="text-left px-4 py-3 font-semibold">Sources</th>
                  <th className="text-left px-4 py-3 font-semibold">Status</th>
                  <th className="text-left px-4 py-3 font-semibold">Last Update</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {filtered.map((l) => (
                  <tr key={l.id} className="hover:bg-white/5 transition-colors" data-testid={`lead-row-${l.id}`}>
                    <td className="px-4 py-3 font-semibold text-white">{l.customer_name}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <a href={`tel:+91${l.mobile_number}`} className="text-cyan-300 hover:underline font-mono">+91 {l.mobile_number}</a>
                    </td>
                    <td className="px-4 py-3 text-slate-300">{l.interested_car || '—'}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {(l.sources || []).map((s, i) => (
                          <span key={i} className="px-2 py-0.5 rounded-full text-[10px] bg-slate-800 text-slate-300 border border-slate-700">
                            {s.source}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3"><StatusPill status={l.status} /></td>
                    <td className="px-4 py-3 text-[11px] text-slate-400 whitespace-nowrap">
                      {l.updated_at ? formatUtcDatetime(l.updated_at) : '—'}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <div className="inline-flex items-center gap-1.5">
                        <a
                          href={`https://wa.me/91${l.mobile_number}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-green-500/20 hover:bg-green-500/30 text-green-300 text-[11px] font-bold"
                          data-testid={`wa-${l.id}`}
                        >
                          <MessageCircle className="w-3 h-3" /> WhatsApp
                        </a>
                        <a
                          href={`tel:+91${l.mobile_number}`}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 text-[11px] font-bold"
                        >
                          <Phone className="w-3 h-3" /> Call
                        </a>
                        <button
                          onClick={() => setUpdateModalLead(l)}
                          data-testid={`crm-update-${l.id}`}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-slate-950 text-[11px] font-bold"
                        >
                          Update
                        </button>
                        {l.status !== 'sold' && (
                          <button
                            onClick={() => setSoldModal({ open: true, lead: l })}
                            data-testid={`mark-sold-${l.id}`}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-purple-500/20 hover:bg-purple-500/30 text-purple-300 text-[11px] font-bold"
                          >
                            <TrendingUp className="w-3 h-3" /> Sold
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <MarkSoldModal
        open={soldModal.open}
        lead={soldModal.lead}
        onClose={() => !soldBusy && setSoldModal({ open: false, lead: null })}
        onConfirm={handleMarkSold}
        busy={soldBusy}
      />

      {updateModalLead && (
        <LeadUpdateModal
          lead={updateModalLead}
          onClose={(fresh) => {
            setUpdateModalLead(null);
            if (fresh) load();
          }}
        />
      )}

      <ManualLeadModal
        open={manualOpen}
        onClose={(created) => {
          setManualOpen(false);
          if (created) load();
        }}
      />
    </div>
  );
};

export default MarketplaceLeads;
